import { useCallback, useEffect, useState } from 'react';

export type ColorScheme = 'light' | 'dark';

const STORAGE_KEY = 'aero.colorScheme';

function hasWindow(): boolean {
  return typeof (globalThis as { window?: unknown }).window !== 'undefined';
}

function getMatchMedia(): ((query: string) => MediaQueryList) | null {
  if (!hasWindow()) return null;
  const mm = (window as Window & { matchMedia?: (q: string) => MediaQueryList }).matchMedia;
  return typeof mm === 'function' ? mm.bind(window) : null;
}

function readStored(): ColorScheme | null {
  if (!hasWindow()) return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : null;
}

function systemPreference(): ColorScheme {
  const mm = getMatchMedia();
  if (!mm) return 'light';
  return mm('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export interface UseColorSchemeResult {
  scheme: ColorScheme;
  setScheme: (next: ColorScheme) => void;
  toggle: () => void;
  /** True if the user has explicitly chosen; false while following system. */
  isExplicit: boolean;
  resetToSystem: () => void;
}

/**
 * Dual-theme state with localStorage persistence and system fallback.
 * - If the user has explicitly chosen, honor it.
 * - Otherwise follow the OS `prefers-color-scheme` and react to changes live.
 * - jsdom / SSR safe: gracefully no-ops when window or matchMedia is missing.
 */
export function useColorScheme(): UseColorSchemeResult {
  const [stored, setStored] = useState<ColorScheme | null>(() => readStored());
  const [systemScheme, setSystemScheme] = useState<ColorScheme>(() => systemPreference());

  useEffect(() => {
    const mm = getMatchMedia();
    if (!mm) return;
    const mq = mm('(prefers-color-scheme: dark)');
    const listener = (e: MediaQueryListEvent) => {
      setSystemScheme(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', listener);
    return () => {
      mq.removeEventListener('change', listener);
    };
  }, []);

  const scheme: ColorScheme = stored ?? systemScheme;

  const setScheme = useCallback((next: ColorScheme) => {
    if (!hasWindow()) return;
    window.localStorage.setItem(STORAGE_KEY, next);
    setStored(next);
  }, []);

  const toggle = useCallback(() => {
    setScheme(scheme === 'dark' ? 'light' : 'dark');
  }, [scheme, setScheme]);

  const resetToSystem = useCallback(() => {
    if (!hasWindow()) return;
    window.localStorage.removeItem(STORAGE_KEY);
    setStored(null);
  }, []);

  return { scheme, setScheme, toggle, isExplicit: stored !== null, resetToSystem };
}
